import { api } from './api';

class NativeAuthService {
    constructor() {
        // Uses the shared api instance with JWT interceptors from api.js
    }

    // Token management
    getToken() {
        return localStorage.getItem('auth_token');
    }

    setToken(token) {
        localStorage.setItem('auth_token', token);
    }

    removeToken() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
    }

    // User management
    getCurrentUser() {
        const userStr = localStorage.getItem('user');
        return userStr ? JSON.parse(userStr) : null;
    }

    setCurrentUser(user) {
        localStorage.setItem('user', JSON.stringify(user));
    }

    // Authentication methods
    async login(email, password) {
        try {
            const response = await api.post('/auth/login', {
                email,
                password
            });

            const { user, token } = response.data;
            
            // Store token and user data
            this.setToken(token);
            this.setCurrentUser(user);

            return { success: true, user, token };
        } catch (error) {
            console.error('Login error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Login failed'
            };
        }
    }

    async logout() {
        try {
            // Call logout endpoint (mainly for logging purposes)
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            // Always clear local storage
            this.removeToken();
        }
    }

    // Password reset methods
    async requestPasswordReset(email) {
        try {
            const response = await api.post('/auth/request-password-reset', {
                email
            });

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Password reset request error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to request password reset'
            };
        }
    }

    async verifyResetToken(token) {
        try {
            const response = await api.get(`/auth/verify-reset-token/${token}`);
            return { success: true, user: response.data.user };
        } catch (error) {
            console.error('Token verification error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Invalid or expired reset token'
            };
        }
    }

    async resetPassword(token, password) {
        try {
            const response = await api.post('/auth/reset-password', {
                token,
                password
            });

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Password reset error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to reset password',
                details: error.response?.data?.details
            };
        }
    }

    async changePassword(currentPassword, newPassword) {
        try {
            const response = await api.post('/auth/change-password', {
                currentPassword,
                newPassword
            });

            return { success: true, message: response.data.message };
        } catch (error) {
            console.error('Change password error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to change password',
                details: error.response?.data?.details
            };
        }
    }

    // Token validation
    async validateToken() {
        try {
            const token = this.getToken();
            if (!token) {
                return { valid: false };
            }

            const response = await api.get('/auth/validate-token');
            return { valid: true, user: response.data.user };
        } catch (error) {
            console.error('Token validation error:', error);
            this.removeToken();
            return { valid: false };
        }
    }

    // Check if user is authenticated
    isAuthenticated() {
        const token = this.getToken();
        const user = this.getCurrentUser();
        return !!(token && user);
    }

    // Check if user is admin
    isAdmin() {
        const user = this.getCurrentUser();
        return user?.role === 'admin';
    }

    // Get current user info from server
    async getCurrentUserFromServer() {
        try {
            const response = await api.get('/auth/me');
            const user = response.data.user;
            this.setCurrentUser(user);
            return { success: true, user };
        } catch (error) {
            console.error('Get current user error:', error);
            return {
                success: false,
                error: error.response?.data?.error || 'Failed to get user info'
            };
        }
    }

    // Token expiration check
    isTokenExpired() {
        const token = this.getToken();
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return Date.now() >= payload.exp * 1000;
        } catch {
            return true;
        }
    }

    // Auto-refresh token if needed (simplified version)
    async refreshTokenIfNeeded() {
        if (this.isTokenExpired()) {
            this.removeToken();
            return false;
        }
        return true;
    }
}

// Create singleton instance
const nativeAuthService = new NativeAuthService();
export default nativeAuthService;